import socket from "../socket";
import store from "../store";
import router from "../router";
import NProgress from "nprogress";
import Vue from "vue";

// Polyfill for EventTarget because Safari has no constructor for it
import EventTarget from "@ungap/event-target";

/**
 * The TeamSpeak Object sends the request to the server and finally receives the response from the ServerQuery.
 * To keep things simple it uses the same naming as the TeamSpeak class from the TS3-NodeJS-Library which is used on the server side.
 * See: https://multivit4min.github.io/TS3-NodeJS-Library/classes/teamspeak.html
 * @type {Object}
 */

const TeamSpeak = Object.create(new EventTarget());

const PERM_MAP = {
  196: "i_client_kick_from_server_power",
  197: "i_client_needed_kick_from_server_power",
  198: "i_client_kick_from_channel_power",
  199: "i_client_needed_kick_from_channel_power",

  200: "i_client_ban_power",
  201: "i_client_needed_ban_power",

  202: "i_client_move_power",
  203: "i_client_needed_move_power",

  204: "i_client_complain_power",
  205: "i_client_needed_complain_power",
};
const permNameCache = Object.create(null);

async function resolvePermName(permid) {
  permid = Number(permid);
  if (!permid) return null;

  if (PERM_MAP[permid]) return PERM_MAP[permid];
  if (permNameCache[permid]) return permNameCache[permid];

  // ✅ burada TeamSpeak.execute YOK!
  return await new Promise((resolve) => {
    socket.emit(
      "teamspeak-execute",
      { command: "permidgetbyid", params: { permid }, options: [] },
      (response) => {
        try {
          if (response && response.id === 0 && response[0]) {
            const name = response[0].permname || response[0].permName;
            if (name) permNameCache[permid] = name;
            resolve(name || null);
          } else {
            resolve(null);
          }
        } catch (e) {
          resolve(null);
        }
      }
    );
  });
}


const trTeamSpeakError = async (err) => {
  const msg = String((err && err.message) || "");

  if (/insufficient client permissions/i.test(msg)) {
    const m = msg.match(/permid\s+(\d+)/i);
    const permid = m ? Number(m[1]) : null;

    // ✅ bağlı değilse perm çözmeye çalışma (loop kesilir)
    if (!store.state.query.connected) {
      return permid ? `Yetkin yok (permid ${permid})` : "Yetkin yok";
    }

    if (permid) {
      const permname = await resolvePermName(permid);
      return permname ? `Yetkin yok (${permname})` : `Yetkin yok (permid ${permid})`;
    }
    return "Yetkin yok";
  }

  if (/flood/i.test(msg)) return "Çok hızlı istek atıyorsun (Flood)";
  return msg;
};


const handleError = async (error, resolve, reject) => {
  // ✅ Promise değil, string
  error.message = await trTeamSpeakError(error);

  if (error.connected) {
    if (error.id === "1281") resolve([]);
    else reject(error);
  } else {
  // ✅ Public sayfalarda login’e zorlama
  const isPublic = window.location.pathname.startsWith("/public/tsview");

  if (!isPublic) {
    store.dispatch("clearStorage");
    router.push({ name: "login" });
  }

  reject(error);
}

};


let handleResponse = async (response, resolve, reject) => {
  if (
    (response.id && response.id !== 0) ||
    (!response.id && response.message)
  ) {
    await handleError(response, resolve, reject);
  } else {
    resolve(response);
  }
};

// Just for debugging the progress bar (NProgress)
const throttleSocketConnection = (time) => {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve();
    }, time);
  });
};

// Middleware that handles the progressbar
let _npTimer = null;
let _npStarted = false;

let _inFlight = 0;

const isPublicRoute = () =>
  typeof window !== "undefined" &&
  window.location &&
  window.location.pathname &&
  window.location.pathname.startsWith("/public/");

const setLoadingState = (methods) => {
  // ✅ Public sayfalarda progress bar tamamen kapalı
  if (isPublicRoute()) return;

  methods.forEach((method) => {
    const next = TeamSpeak[method];

    // güvenlik
    if (typeof next !== "function") return;

    // ✅ ÇİFT WRAP ENGELİ
    if (next.__wrapped) return;

    const wrapped = async (...args) => {
      _inFlight++;
      store.commit("isLoading", true);

      // ✅ sadece ilk istekte başlat
      if (_inFlight === 1) NProgress.start();

      try {
        return await next(...args);
      } finally {
        _inFlight = Math.max(0, _inFlight - 1);

        if (_inFlight === 0) {
          store.commit("isLoading", false);
          NProgress.done(true);
        }
      }
    };

    wrapped.__wrapped = true;
    TeamSpeak[method] = wrapped;
  });
};


TeamSpeak.connect = (params) => {
	if (isPublicRoute()) {
    return Promise.reject(new Error("Public route: TeamSpeak socket disabled"));
  }
  return new Promise((resolve, reject) => {
    socket.emit("teamspeak-connect", params, (response) => {
      if (response.token) {
        resolve(response);
      } else {
        reject(response);
      }
    });
  });
};

let _execInFlight = 0;

TeamSpeak.execute = (...args) => {
	 if (isPublicRoute()) {
    return Promise.reject(new Error("Public route: TeamSpeak socket disabled"));
  }
  let command = args[0];
  if (!command || typeof command !== "string") {
    return Promise.reject(new Error("TeamSpeak.execute called without command"));
  }

  let params = args[1] ? args[1] : {};
  let options = args[2] ? args[2] : [];

  return new Promise((resolve, reject) => {
    const timeoutMs = 12000; // ✅ 6s -> 12s

    _execInFlight++;

    const t = setTimeout(() => {
      _execInFlight = Math.max(0, _execInFlight - 1);
      reject(new Error("Socket timeout: teamspeak-execute response gelmedi"));
    }, timeoutMs);

    socket.emit(
      "teamspeak-execute",
      { command, params, options },
      async (response) => {
        clearTimeout(t);
        _execInFlight = Math.max(0, _execInFlight - 1);
        await handleResponse(response, resolve, reject);
      }
    );
  });
};

TeamSpeak.createSnapshot = () => {
  return new Promise((resolve, reject) => {
    socket.emit("teamspeak-createsnapshot", (response) =>
      handleResponse(response, resolve, reject)
    );
  });
};

TeamSpeak.deploySnapshot = (snapshot) => {
  return new Promise((resolve, reject) => {
    socket.emit("teamspeak-deploysnapshot", snapshot, (response) =>
      handleResponse(response, resolve, reject)
    );
  });
};

// The ServerQuery returns maximum 200 entries in the clientdblist.
// This function collects all available entries in the client database list.
TeamSpeak.fullClientDBList = async () => {
  let fullClientDbList = [];
  let start = 0;
  let duration = 200;

  try {
    while (
      (
        await TeamSpeak.execute("clientdblist", {
          start,
          duration,
        })
      ).length
    ) {
      fullClientDbList.push(
        ...(await TeamSpeak.execute("clientdblist", {
          start,
          duration,
        }))
      );

      start += 200;
      duration += 200;
    }
  } catch (err) {
    throw err;
  }

  return fullClientDbList;
};

// To-Do: After 400 lines flooding warnings
TeamSpeak.fullLogView = async (instance = 0) => {
  let allLogs = [];
  let lastPosition = undefined;

  try {
    while (lastPosition !== 0) {
      let logs = await TeamSpeak.execute("logview", {
        instance,
        reverse: 1,
        lines: 100,
        beginPos: lastPosition,
      });

      lastPosition = logs[0].lastPos;

      allLogs.push(...logs);
    }
  } catch (err) {
    throw err;
  }

  return allLogs;
};

TeamSpeak.registerEvents = () => {
  return new Promise((resolve, reject) => {
    socket.emit("teamspeak-registerevents", (response) =>
      handleResponse(response, resolve, reject)
    );
  });
};

TeamSpeak.unregisterEvent = () => {
  return new Promise((resolve, reject) => {
    socket.emit("teamspeak-unregisterevent", (response) =>
      handleResponse(response, resolve, reject)
    );
  });
};

TeamSpeak.selectServer = (sid) => {
  return TeamSpeak.execute("use", { sid })
    .then(() => store.dispatch("saveServerId", sid))
    .then(() => TeamSpeak.registerEvents())
    .then(() => TeamSpeak.execute("whoami"))
    .then((userInfo) => store.commit("saveUserInfo", userInfo[0]));
};

TeamSpeak.downloadFile = (path, cid, cpw = "") => {
  return new Promise((resolve, reject) => {
    socket.emit("teamspeak-downloadfile", { path, cid, cpw }, (response) => {
      handleResponse(response, resolve, reject);
    });
  });
};

TeamSpeak.reconnect = () => {
  return new Promise((resolve, reject) => {
    socket.emit(
      "teamspeak-reconnect",
      {
        token: store.state.query.token,
        serverId: store.state.query.serverId,
      },
      async (res) => {
        if (res.reconnected) {
          try {
            let queryUser = await TeamSpeak.execute("whoami").then(
              (list) => list[0]
            );

            store.dispatch("saveConnection", { queryUser, connected: true });
          } catch (err) {
            reject(err);
          }

          resolve();
        } else {
          reject(res);
        }
      }
    );
  });
};

TeamSpeak.on = (name, fn) => {
  TeamSpeak.__proto__.addEventListener(name, fn);
};

socket.on("teamspeak-textmessage", (data) => {
  TeamSpeak.__proto__.dispatchEvent(
    new CustomEvent("textmessage", {
      detail: data,
    })
  );
});

socket.on("teamspeak-clientconnect", (data) => {
  TeamSpeak.__proto__.dispatchEvent(
    new CustomEvent("clientconnect", {
      detail: data,
    })
  );
});

socket.on("teamspeak-clientdisconnect", (data) => {
  TeamSpeak.__proto__.dispatchEvent(
    new CustomEvent("clientdisconnect", {
      detail: data,
    })
  );
});

socket.on("teamspeak-clientmoved", (data) => {
  TeamSpeak.__proto__.dispatchEvent(
    new CustomEvent("clientmoved", {
      detail: data,
    })
  );
});

socket.on("teamspeak-tokenused", (data) => {
  TeamSpeak.__proto__.dispatchEvent(
    new CustomEvent("tokenused", {
      detail: data,
    })
  );
});
socket.on("teamspeak-clientupdated", (data) => {
  TeamSpeak.__proto__.dispatchEvent(
    new CustomEvent("clientupdated", { detail: data })
  );
});

socket.on("teamspeak-serveredit", (data) => {
  TeamSpeak.__proto__.dispatchEvent(
    new CustomEvent("serveredit", {
      detail: data,
    })
  );
});

socket.on("teamspeak-channeledit", (data) => {
  TeamSpeak.__proto__.dispatchEvent(
    new CustomEvent("channeledit", {
      detail: data,
    })
  );
});

socket.on("teamspeak-channelcreate", (data) => {
  TeamSpeak.__proto__.dispatchEvent(
    new CustomEvent("channelcreate", {
      detail: data,
    })
  );
});

socket.on("teamspeak-channelmoved", (data) => {
  TeamSpeak.__proto__.dispatchEvent(
    new CustomEvent("channelmoved", {
      detail: data,
    })
  );
});

socket.on("teamspeak-channeldelete", (data) => {
  TeamSpeak.__proto__.dispatchEvent(
    new CustomEvent("channeldelete", {
      detail: data,
    })
  );
});

// When the teamspeak connection is closed manually.
// E.g. writing "quit" in the console
socket.on("teamspeak-disconnect", () => {
  store.dispatch("clearStorage");

  // ✅ public route ise login'e atma
  if (router.currentRoute.path.startsWith("/public/")) {
    return;
  }

  router.push({ name: "login" });
});
socket.on("disconnect", () => {
  _inFlight = 0;
  store.commit("isLoading", false);
  NProgress.done(true);
});


setLoadingState([
  "execute",
  "createSnapshot",
  "deploySnapshot",
  "selectServer",
]);

export default TeamSpeak;
